// @figma file=tbd node=tbd name="UserAvatarMenu" set="tbd" verified=tbd

import { cva, type VariantProps } from 'class-variance-authority';
import { LogOutIcon, SettingsIcon, UserIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../atoms/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';

// ─── Trigger size variants ────────────────────────────────────────────────────

export const userAvatarMenuVariants = cva('', {
  variants: {
    /**
     * Size — maps to Avatar size prop. Controls the clickable trigger avatar.
     * sm → 32px · default → 40px · lg → 48px
     */
    size: {
      sm: 'sm' as const,
      default: 'md' as const,
      lg: 'lg' as const,
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

// ─── MenuItem types ───────────────────────────────────────────────────────────

export interface UserAvatarMenuAction {
  /** Unique key for the action */
  key: string;
  /** Display label */
  label: string;
  /** Optional Lucide icon element */
  icon?: React.ReactNode;
  /** Marks this item as destructive (Logout). Renders in destructive foreground. */
  destructive?: boolean;
  /** Callback when the item is selected */
  onSelect?: () => void;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface UserAvatarMenuProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'>,
    Omit<VariantProps<typeof userAvatarMenuVariants>, 'size'> {
  /** User display name shown as the dropdown section label */
  userName?: string;
  /** User email shown below the name in the header */
  userEmail?: string;
  /** Image URL for the avatar */
  avatarSrc?: string;
  /** Fallback initials when no image is provided */
  avatarFallback?: string;
  /** Avatar size — sm/default/lg */
  size?: 'sm' | 'default' | 'lg';
  /**
   * Menu items. Defaults to Profile, Settings, and Logout when omitted.
   * Pass an explicit array to override the full item list.
   */
  items?: UserAvatarMenuAction[];
  /** Callback for the Profile action (default item) */
  onProfile?: () => void;
  /** Callback for the Settings action (default item) */
  onSettings?: () => void;
  /** Callback for the Logout action (default item) */
  onLogout?: () => void;
  /** Controls whether the dropdown is open (controlled mode) */
  open?: boolean;
  /** Callback when open state changes (controlled mode) */
  onOpenChange?: (open: boolean) => void;
  /** Alignment of the dropdown relative to the trigger */
  align?: 'start' | 'center' | 'end';
}

// ─── UserAvatarMenu ───────────────────────────────────────────────────────────

/**
 * Dropdown menu triggered by a user avatar. Provides Profile, Settings, and
 * Logout actions by default, with support for a custom item list.
 *
 * Composes `Avatar` (atom) and `DropdownMenu` (molecule) from the design system.
 *
 * @example
 * ```tsx
 * <UserAvatarMenu
 *   userName="Jane Doe"
 *   userEmail="jane@example.com"
 *   avatarSrc="/avatars/jane.jpg"
 *   avatarFallback="JD"
 *   onProfile={() => router.push('/profile')}
 *   onSettings={() => router.push('/settings')}
 *   onLogout={() => signOut()}
 * />
 * ```
 */
const UserAvatarMenu = React.forwardRef<HTMLDivElement, UserAvatarMenuProps>(
  (
    {
      className,
      userName,
      userEmail,
      avatarSrc,
      avatarFallback = '?',
      size = 'default',
      items,
      onProfile,
      onSettings,
      onLogout,
      open,
      onOpenChange,
      align = 'end',
      ...props
    },
    ref,
  ) => {
    // Map size → Avatar size prop
    const avatarSizeMap = {
      sm: 'sm',
      default: 'md',
      lg: 'lg',
    } as const;

    // Default actions when no custom items are supplied
    const defaultItems: UserAvatarMenuAction[] = [
      {
        key: 'profile',
        label: 'Profile',
        icon: <UserIcon className="h-4 w-4" aria-hidden="true" />,
        onSelect: onProfile,
      },
      {
        key: 'settings',
        label: 'Settings',
        icon: <SettingsIcon className="h-4 w-4" aria-hidden="true" />,
        onSelect: onSettings,
      },
      {
        key: 'logout',
        label: 'Log out',
        icon: <LogOutIcon className="h-4 w-4" aria-hidden="true" />,
        destructive: true,
        onSelect: onLogout,
      },
    ];

    const menuItems = items ?? defaultItems;

    return (
      <div ref={ref} className={cn('inline-flex', className)} {...props}>
        <DropdownMenu open={open} onOpenChange={onOpenChange}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={userName ? `Open menu for ${userName}` : 'Open user menu'}
            >
              <Avatar size={avatarSizeMap[size]} shape="round">
                {avatarSrc && <AvatarImage src={avatarSrc} alt={userName ?? 'User avatar'} />}
                <AvatarFallback>{avatarFallback}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align={align} className="w-56">
            {(userName || userEmail) && (
              <>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-0.5">
                    {userName && (
                      <p className="text-sm font-semibold text-foreground leading-none">
                        {userName}
                      </p>
                    )}
                    {userEmail && (
                      <p className="text-xs text-muted-foreground leading-none truncate">
                        {userEmail}
                      </p>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )}

            {menuItems.map((item) => (
              <DropdownMenuItem
                key={item.key}
                onSelect={item.onSelect}
                className={cn(
                  'flex items-center gap-2',
                  item.destructive &&
                    'text-destructive focus:text-destructive focus:bg-destructive/10',
                )}
              >
                {item.icon}
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  },
);
UserAvatarMenu.displayName = 'UserAvatarMenu';

export { UserAvatarMenu };
